import { HttpErrorResponse, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { catchError, throwError } from 'rxjs';
import { ClientAccessScopeService } from '../services/client-access-scope.service';
import { MenuPermissionService } from '../services/menu-permission.service';
import { SiteStateService } from '../services/site-state.service';
import { environment } from '../../../environments/environment';

/** แสดงข้อความบนหน้า login หลัง session ถูกปิด */
export const ADMIN_SESSION_END_REASON_KEY = 'admin_session_end_reason';

/** เวลาใช้งานล่าสุดของผู้ใช้ (ms) — แชร์ทุกแท็บผ่าน localStorage (เขียนโดย IdleServiceService) */
export const ADMIN_LAST_ACTIVITY_KEY = 'admin_last_activity_at';

/** ไม่มี input จากผู้ใช้เกินนี้ → request ถือเป็น background (API ไม่ต่ออายุ session) */
const BACKGROUND_AFTER_MS = 60 * 1000;
const BACKGROUND_REQUEST_HEADER = 'X-Background-Request';

export function readAdminLastActivity(): number {
  try {
    return Number(localStorage.getItem(ADMIN_LAST_ACTIVITY_KEY)) || 0;
  } catch {
    return 0;
  }
}

function markBackgroundIfUserIdle(req: HttpRequest<unknown>): HttpRequest<unknown> {
  if (!req.url.startsWith(environment.apiPrivateUrl)) return req;
  if (Date.now() - readAdminLastActivity() < BACKGROUND_AFTER_MS) return req;
  return req.clone({ setHeaders: { [BACKGROUND_REQUEST_HEADER]: '1' } });
}

export function consumeAdminSessionEndReason(): 'session_expired' | null {
  try {
    const value = sessionStorage.getItem(ADMIN_SESSION_END_REASON_KEY);
    if (value === 'session_expired') {
      sessionStorage.removeItem(ADMIN_SESSION_END_REASON_KEY);
      return 'session_expired';
    }
  } catch {
    // ignore
  }
  return null;
}

function isPublicAuthRequest(url: string): boolean {
  return (
    url.includes('/Auth/Login') ||
    url.includes('/Auth/setFirstPassword') ||
    url.includes('/Auth/Logout')
  );
}

function clearAdminAuthState(
  cookieService: CookieService,
  siteState: SiteStateService,
  clientScope: ClientAccessScopeService,
  permissionService: MenuPermissionService,
  platformId: object,
): void {
  siteState.clear();
  clientScope.clear();
  permissionService.resetSession();

  const path = '/';
  const keys = ['token', 'user_id', 'username', 'name', 'company_id', 'must_change_password'];
  for (const key of keys) {
    cookieService.delete(key, path);
  }
  cookieService.deleteAll(path);

  if (isPlatformBrowser(platformId)) {
    try {
      localStorage.removeItem('userId');
      localStorage.removeItem('user_id');
    } catch {
      // ignore
    }
  }
}

/**
 * 401 จาก API (session ถูกปิด / idle) → ไปหน้า login
 */
export const authSessionInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);
  const cookieService = inject(CookieService);
  const siteState = inject(SiteStateService);
  const clientScope = inject(ClientAccessScopeService);
  const permissionService = inject(MenuPermissionService);
  const platformId = inject(PLATFORM_ID);

  return next(markBackgroundIfUserIdle(req)).pipe(
    catchError((err: unknown) => {
      if (!(err instanceof HttpErrorResponse) || err.status !== 401) {
        return throwError(() => err);
      }
      if (isPublicAuthRequest(req.url)) {
        return throwError(() => err);
      }
      if (router.url.includes('/login')) {
        return throwError(() => err);
      }

      try {
        sessionStorage.setItem(ADMIN_SESSION_END_REASON_KEY, 'session_expired');
      } catch {
        // ignore
      }

      clearAdminAuthState(cookieService, siteState, clientScope, permissionService, platformId);
      router.navigate(['/login'], { replaceUrl: true });
      return throwError(() => err);
    }),
  );
};
